import { Injectable, OnDestroy } from '@angular/core';

interface PollingRegistration {
  callback: () => void;
  intervalMs: number;
  timerId: ReturnType<typeof setInterval> | null;
}

/**
 * Servicio centralizado de polling que respeta la Page Visibility API.
 *
 * - Cuando la pestaña se oculta: pausa todos los intervals registrados.
 * - Cuando la pestaña vuelve a ser visible: ejecuta inmediatamente cada
 *   callback (para refrescar datos) y reinicia los intervals.
 *
 * Uso:
 *   constructor(private polling: VisibilityPollingService) {}
 *   ngOnInit()    { this.polling.register('orders-list', () => this.refresh(), 40000); }
 *   ngOnDestroy() { this.polling.unregister('orders-list'); }
 */
@Injectable({ providedIn: 'root' })
export class VisibilityPollingService implements OnDestroy {
  private registrations = new Map<string, PollingRegistration>();
  private onVisibilityChangeBound = this.onVisibilityChange.bind(this);

  constructor() {
    document.addEventListener('visibilitychange', this.onVisibilityChangeBound);
  }

  /**
   * Registra un callback de polling. Si ya existe una key con el mismo nombre,
   * se limpia la anterior antes de registrar la nueva.
   */
  register(key: string, callback: () => void, intervalMs: number): void {
    this.unregister(key);

    const reg: PollingRegistration = { callback, intervalMs, timerId: null };

    if (!document.hidden) {
      reg.timerId = setInterval(callback, intervalMs);
    }

    this.registrations.set(key, reg);
  }

  /** Detiene y elimina un polling registrado. */
  unregister(key: string): void {
    const reg = this.registrations.get(key);
    if (reg) {
      if (reg.timerId !== null) clearInterval(reg.timerId);
      this.registrations.delete(key);
    }
  }

  /** Devuelve true si la pestaña está actualmente oculta. */
  get isHidden(): boolean {
    return document.hidden;
  }

  private onVisibilityChange(): void {
    if (document.hidden) {
      this.pauseAll();
    } else {
      this.resumeAll();
    }
  }

  private pauseAll(): void {
    this.registrations.forEach(reg => {
      if (reg.timerId !== null) {
        clearInterval(reg.timerId);
        reg.timerId = null;
      }
    });
  }

  private resumeAll(): void {
    this.registrations.forEach(reg => {
      // Ejecutar inmediatamente para refrescar datos al volver
      try { reg.callback(); } catch (_) {}
      reg.timerId = setInterval(reg.callback, reg.intervalMs);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChangeBound);
    this.registrations.forEach(reg => {
      if (reg.timerId !== null) clearInterval(reg.timerId);
    });
    this.registrations.clear();
  }
}
